import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dropdown, Form, Modal, OverlayTrigger, Placeholder, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import DstAlert from "../../../../components/DstAlert/DstAlert";
import DstButton from "../../../../components/DstButton/DstButton";
import DstTable from "../../../../components/DstTable/DstTable";
import DstTableSearch from "../../../../components/DstTable/DstTableSearch/DstTableSearch";
import MultiSelectDropdown from "../../../../components/MultiSelectDropdown/MultiSelectDropdown";
import MultiSelectLiveDropdown from "../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import Popup from "../../../../components/Popup/Popup";
import RequiredLabel from "../../../../components/RequiredLabel/RequiredLabel";
import { useSession } from "../../../../contexts/SessionContext";
import { PermissionComponent } from "../../../../services/PermissionGuard";
import { daysMapping, formatDateHumanly, formatToISODateTime, isPast } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import { completeNavigate } from "../../../../services/UtilityService";
import {
  Animator,
  CohortMeetingsProps,
  FormData,
  LanguageKeys,
  MeetingLinkService,
  MeetingTypes,
  Program,
  VisibilityKeys,
} from "./CohortMeetings.d";
import {
  createMeeting,
  fetchMeetingTypes,
  getAdmins,
  getMeetings,
  getSuggestedNames,
  styleMeetingsRow,
} from "./CohortMeetings.function";
import "./CohortMeetings.scss";

const CohortMeetings = ({ cohortId, cohortLanguage, language, onCountLoaded }: CohortMeetingsProps) => {
  const navigate = useNavigate();
  const { email } = useSession();
  const [animators, setAnimators] = useState<Animator[]>([]);
  const [duration, setDuration] = useState(120);
  const [durationError, setDurationError] = useState(false);
  const [meetings, setMeetings] = useState<any[]>([]);
  const [meetingLinkService, setMeetingLinkService] = useState<MeetingLinkService>("zoom");
  const [isFormValid, setIsFormValid] = useState(false);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [selectedAnimators, setSelectedAnimators] = useState<Animator[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageKeys>(cohortLanguage as LanguageKeys);
  const [selectedMeetingType, setSelectedMeetingType] = useState<MeetingTypes | null>(null);
  const [selectedProgram, setSelectedProgram] = useState<any>(null);
  const [selectedVisibility, setSelectedVisibility] = useState<VisibilityKeys>("shared");
  const [showModal, setShowModal] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [meetingTypes, setMeetingTypes] = useState<MeetingTypes[]>([]);
  const [selectedProject, setSelectedProject] = useState<{ id: number; name: string } | null>(null);
  const [afternoonEnd, setAfternoonEnd] = useState<string>("");
  const [afternoonStart, setAfternoonStart] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [morningEnd, setMorningEnd] = useState<string>("");
  const [morningStart, setMorningStart] = useState<string>("");
  const [selectedDays, setSelectedDays] = useState<{ id: string; day: string }[]>([]);
  const [isMeetingCreating, setIsMeetingCreating] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertProps, setAlertProps] = useState<any>({});

  const displayAlert = (props: any) => {
    setAlertProps(props);
    setShowAlert(true);
    setShowModal(false);
  };

  const _handleAlertClose = () => {
    setShowAlert(false);
    setLoading(true);
    resetSelection();
    getMeetings(
      cohortId,
      (meetings: any[]) => {
        setMeetings(meetings);
        onCountLoaded?.(meetings.length);
      },
      language,
      setLoading
    );
  };

  const columns = [
    { name: translate(language, "GLOBAL.NAME"), key: "name" },
    {
      name: translate(language, "GLOBAL.START_DATE"),
      key: "dt_start",
      render: (item: any) => formatDateHumanly(language, item.dt_start, true),
      sortCompute: (item: any) => formatToISODateTime(item.dt_start),
    },
    {
      name: translate(language, "GLOBAL.END_DATE"),
      key: "dt_end",
      render: (item: any) => formatDateHumanly(language, item.dt_end, true),
      sortCompute: (item: any) => formatToISODateTime(item.dt_end),
    },
    {
      name: translate(language, "PAGES.COHORT.MEETINGS.FIELDS.TYPE"),
      key: "type.name",
      render: (item: any) => item.type.name,
    },
    { name: translate(language, "PAGES.COHORT.MEETINGS.FIELDS.ATTENDANCE_RECORD"), key: "attendance_record" },
    { name: translate(language, "PAGES.COHORT.MEETINGS.FIELDS.VISIBILITY"), key: "visibility" },
    {
      name: translate(language, "GLOBAL.ORGANIZER"),
      key: "author",
      render: (item: any) => item.author.full_name,
    },
    {
      name: translate(language, "GLOBAL.ANIMATORS"),
      key: "animators",
      render: (item: any) =>
        item.animators ? item.animators.map((animator: Animator) => animator.email).join(", ") : "",
    },
  ];
  const preProcessedData = meetings?.map((item: any) => ({ ...item, columns }));

  const languageMapping: Record<LanguageKeys, string> = {
    "fr-FR": translate(language, "PAGES.COHORT.MEETINGS.MODAL.OPTIONS.LANGUAGE.FRENCH"),
    "en-US": translate(language, "PAGES.COHORT.MEETINGS.MODAL.OPTIONS.LANGUAGE.ENGLISH"),
  };

  const visibilityOptions: Record<VisibilityKeys, string> = {
    shared: translate(language, "PAGES.COHORT.MEETINGS.MODAL.OPTIONS.VISIBILITY.SHARED"),
    private: translate(language, "PAGES.COHORT.MEETINGS.MODAL.OPTIONS.VISIBILITY.PRIVATE"),
  };

  useEffect(() => {
    getAdmins(setAnimators, setPrograms, language);
    fetchMeetingTypes(setMeetingTypes, language);
    getMeetings(
      cohortId,
      (meetings: any[]) => {
        setMeetings(meetings);
        onCountLoaded?.(meetings.length);
      },
      language,
      setLoading
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortId, language]);

  const resetSelection = () => {
    setDuration(120);
    setDurationError(false);
    setMeetingLinkService("zoom");
    setSelectedAnimators([]);
    setSelectedLanguage(cohortLanguage as LanguageKeys);
    setSelectedProgram(null);
    setSelectedVisibility("shared");
    setStartDate("");
    setTitle("");
    setSelectedMeetingType(
      meetingTypes.find((meetingType) => meetingType.name.toLowerCase() === "masterclass") || null
    );
    setSelectedProject(null);
    setAfternoonEnd("");
    setAfternoonStart("");
    setEndDate("");
    setMorningEnd("");
    setMorningStart("");
    setSelectedDays([]);
    setUrl("");
    setDescription("");
  };

  const validateForm = useCallback(() => {
    let isValid =
      !!selectedMeetingType &&
      !!title &&
      !!startDate &&
      !isPast(startDate) &&
      !!selectedLanguage &&
      !!selectedProgram &&
      duration > 0 &&
      !!selectedVisibility &&
      !durationError;

    if (selectedMeetingType?.need_project) {
      isValid = isValid && !!selectedProject;
    }

    switch (selectedMeetingType?.name.toLowerCase()) {
      case "multiple days": {
        const hasMorning = !!morningStart && !!morningEnd;
        const hasAfternoon = !!afternoonStart && !!afternoonEnd;

        isValid = isValid && !!endDate && selectedDays.length > 0 && (hasMorning || hasAfternoon);
        break;
      }
      default:
        isValid = isValid && !!duration;
        break;
    }

    setIsFormValid(isValid);
  }, [
    selectedMeetingType,
    title,
    startDate,
    selectedLanguage,
    selectedProgram,
    duration,
    selectedVisibility,
    durationError,
    selectedProject,
    endDate,
    selectedDays.length,
    morningStart,
    morningEnd,
    afternoonStart,
    afternoonEnd,
  ]);

  useEffect(() => {
    validateForm();
  }, [
    selectedMeetingType,
    selectedProgram,
    selectedLanguage,
    selectedProject,
    title,
    startDate,
    duration,
    selectedVisibility,
    durationError,
    validateForm,
  ]);

  useEffect(() => {
    setSelectedMeetingType(
      meetingTypes.find((meetingType) => meetingType.name.toLowerCase() === "masterclass") || null
    );
  }, [meetingTypes]);

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const parsedValue = parseInt(value, 10);

    if (parsedValue > 0) {
      setDurationError(false);
      setDuration(parsedValue);
    } else {
      setDurationError(true);
      setDuration(0);
    }
  };

  const suggestionNames = useMemo(
    () =>
      getSuggestedNames(selectedMeetingType, selectedProgram?.id, selectedLanguage).map((name, index) => (
        <option key={index} value={name} />
      )),
    [selectedLanguage, selectedMeetingType, selectedProgram?.id]
  );

  const handleConfirm = () => {
    const formData: FormData = {
      selectedMeetingType,
      selectedProgram: selectedProgram?.id,
      selectedLanguage,
      title,
      startDate,
      duration,
      selectedVisibility,
      selectedAnimators,
      meetingLinkService,
      selectedProject: selectedProject?.id || null,
      afternoonEnd,
      afternoonStart,
      endDate,
      morningEnd,
      morningStart,
      selectedDays,
      description,
      url,
    };
    createMeeting(cohortId, formData, language, setIsMeetingCreating, displayAlert, setShowModal, resetSelection);
  };

  const handleRefresh = () => {
    getMeetings(cohortId, setMeetings, language, setLoading);
  };

  const getDaysOptions = (language: string) =>
    Object.entries(daysMapping(language))
      .filter(([_, value]) => value !== undefined)
      .map(([key, day]) => ({
        id: key,
        day: day!,
      }));

  if (loading) {
    return (
      <div className="container-meetings-cohort px-3">
        <div className="d-flex justify-content-between mb-3">
          <div className="d-flex">
            <h2 className="text-center me-3">{translate(language, "PAGES.COHORT.MEETINGS.TITLE")}</h2>
            <DstTableSearch data={preProcessedData} searchableColumns={["organizer_id"]} onSearch={setFilteredData} />
          </div>
          <div className="d-flex">
            <DstButton
              variant="light"
              value={translate(language, "PAGES.COHORT.MEETINGS.BUTTONS.REFRESH")}
              mock
              btnWidth="88px"
              btnImageBefore={<span className="dst-icon-refresh-cw-04 pe-2 align-text-bottom" />}
            />
            <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/meetings" }]}>
              <DstButton
                btnClass="ms-2"
                value={translate(language, "PAGES.COHORT.MEETINGS.BUTTONS.ADD")}
                mock
                btnWidth="115px"
                btnImageBefore={<span className="dst-icon-plus pe-2 align-text-bottom" />}
              />
            </PermissionComponent>
          </div>
        </div>
        <Placeholder as="div" animation="glow">
          <Placeholder xs={12} className="w-100 mb-3" style={{ height: "50px" }} />
          <Placeholder xs={12} className="w-100 mb-3" style={{ height: "50px" }} />
          <Placeholder xs={12} className="w-100 mb-3" style={{ height: "50px" }} />
        </Placeholder>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="container-meetings-cohort px-3">
        <div className="d-flex justify-content-between mb-3">
          <div className="d-flex">
            <h2 className="text-center me-3">
              {translate(language, "PAGES.COHORT.MEETINGS.TITLE")} ({meetings.length})
            </h2>
            <DstTableSearch
              data={preProcessedData}
              searchableColumns={[
                "meeting_name",
                "start",
                "end",
                "meeting_type_name",
                "attendance_record",
                "visibility",
                "organizer_id",
                "animators",
              ]}
              onSearch={setFilteredData}
            />
          </div>
          <div className="d-flex">
            <DstButton
              variant="light"
              value={translate(language, "PAGES.COHORT.MEETINGS.BUTTONS.REFRESH")}
              btnImageBefore={<span className="dst-icon-refresh-cw-04 pe-2 align-text-bottom" />}
              clickFunction={handleRefresh}
            />
            <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/cohorts/*/meetings" }]}>
              <DstButton
                btnClass="ms-2"
                value={translate(language, "PAGES.COHORT.MEETINGS.BUTTONS.ADD")}
                btnImageBefore={<span className="dst-icon-plus pe-2 align-text-bottom" />}
                clickFunction={() => setShowModal(true)}
              />
            </PermissionComponent>
          </div>
        </div>
        {meetings.length > 0 ? (
          <DstTable
            data={filteredData.length > 0 ? filteredData : preProcessedData}
            striped={false}
            columns={columns}
            classLine={(meetings) => styleMeetingsRow(meetings)}
            onRowClick={(event, item) => completeNavigate(event, navigate, `/meeting/${item.id}`)}
          />
        ) : (
          <div className="mt-3 text-muted">{translate(language, "PAGES.COHORT.MEETINGS.NO_MEETINGS_FOUND")}</div>
        )}
        <Popup
          show={showModal}
          onClose={() => setShowModal(false)}
          modalTitle={translate(language, "PAGES.COHORT.MEETINGS.MODAL.TITLE")}
          scrollable
        >
          <Form id="modal-add-meeting">
            <Form.Group>
              <Form.Label>
                <RequiredLabel label={translate(language, "PAGES.COHORT.MEETINGS.MODAL.LABELS.MEETING_TYPE")} />
              </Form.Label>
              {meetingTypes.length > 0 ? (
                <Dropdown>
                  <Dropdown.Toggle
                    className="w-100 d-flex justify-content-between align-items-center border-secondary"
                    variant=""
                  >
                    {selectedMeetingType?.name}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    {meetingTypes.map((type) => (
                      <Dropdown.Item key={type.id} onClick={() => setSelectedMeetingType(type)}>
                        {type.name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              ) : (
                <Placeholder animation="glow">
                  <Placeholder.Button className="w-100" />
                </Placeholder>
              )}
            </Form.Group>

            <React.Fragment>
              <Form.Group className="mt-3">
                <Form.Label>
                  <RequiredLabel label={translate(language, "PAGES.COHORT.MEETINGS.MODAL.LABELS.PROGRAM")} />
                </Form.Label>
                <MultiSelectDropdown
                  label={selectedProgram?.name || translate(language, "PAGES.COHORT.MEETINGS.MODAL.LABELS.PROGRAM")}
                  items={programs}
                  displayItem={(item) => item.name}
                  badgeItemProperty="name"
                  onConfirm={(selectedItems) => setSelectedProgram(selectedItems?.[0] || null)}
                  language={language}
                  isSingleSelect
                  toggleClass={`text-truncate border-secondary w-100 ${!selectedProgram ? "text-muted" : ""}`}
                  btnWidth="100%"
                  mock={!programs.length}
                />
              </Form.Group>
              <Form.Group className="mt-3">
                <Form.Label>
                  <RequiredLabel label={translate(language, "GLOBAL.LANGUAGE")} />
                </Form.Label>
                <Dropdown>
                  <Dropdown.Toggle
                    className="w-100 d-flex justify-content-between align-items-center border-secondary"
                    variant=""
                  >
                    {languageMapping[selectedLanguage]}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    {Object.keys(languageMapping).map((key) => (
                      <Dropdown.Item key={key} onClick={() => setSelectedLanguage(key as LanguageKeys)}>
                        {languageMapping[key as LanguageKeys]}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Form.Group>
            </React.Fragment>

            {selectedMeetingType?.need_project && (
              <Form.Group className="mt-3">
                <Form.Label>
                  <RequiredLabel label={translate(language, "PAGES.COHORT.MEETINGS.MODAL.LABELS.PROJECT")} />
                </Form.Label>
                <MultiSelectLiveDropdown
                  label={
                    selectedProject
                      ? `${translate(language, "PAGES.COHORT.MEETINGS.MODAL.PLACEHOLDERS.PROJECT_SELECTED")} ${
                          selectedProject.name
                        }`
                      : translate(language, "PAGES.COHORT.MEETINGS.MODAL.PLACEHOLDERS.SELECT_PROJECT")
                  }
                  category={["projects"]}
                  language={language}
                  isSingleSelect
                  initialSelectedItems={selectedProject ? [{ category: "projects", value: selectedProject }] : []}
                  onConfirm={(selected) => {
                    if (selected.length > 0) {
                      const projectId = +selected[0].value.id;
                      setSelectedProject({ id: projectId, name: selected[0].value.name });
                    } else {
                      setSelectedProject(null);
                    }
                  }}
                  toggleClass={`text-truncate w-100 border-secondary ${!selectedProject ? "text-muted" : ""}`}
                />
              </Form.Group>
            )}

            <Form.Group className="mt-3">
              <Form.Label>
                <RequiredLabel label={translate(language, "GLOBAL.TITLE")} />
              </Form.Label>
              <Form.Control
                className={`border-secondary ${!title ? "text-muted" : ""}`}
                type="text"
                placeholder={translate(language, "PAGES.COHORT.MEETINGS.MODAL.PLACEHOLDERS.TITLE_SELECT")}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                list="suggested-title-list"
              />
              <datalist id="suggested-title-list">{suggestionNames}</datalist>
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>
                <RequiredLabel label={translate(language, "GLOBAL.START_DATE")} />
              </Form.Label>
              <Form.Control
                className={`border-secondary ${!startDate ? "text-muted" : ""}`}
                type="datetime-local"
                min={formatToISODateTime(new Date().toISOString(), true).replace(" ", "T")}
                value={startDate || ""}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </Form.Group>
            {selectedMeetingType?.name.toLowerCase() === "multiple days" ? (
              <React.Fragment>
                <Form.Group className="mt-3">
                  <Form.Label>
                    <RequiredLabel label={translate(language, "GLOBAL.END_DATE")} />
                  </Form.Label>
                  <Form.Control
                    className={`border-secondary ${!endDate ? "text-muted" : ""}`}
                    type="datetime-local"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mt-3">
                  <Form.Label>
                    {translate(language, "PAGES.COHORT.MEETINGS.MODAL.LABELS.MORNING_START")}{" "}
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Tooltip id="time-slots-tooltip">
                          {translate(language, "PAGES.COHORT.MEETINGS.MODAL.LABELS.TIME_SLOTS_INFO")}
                        </Tooltip>
                      }
                    >
                      <span className="dst-icon-info-circle ms-1 text-muted clickable" />
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Control
                    className={`border-secondary ${!morningStart ? "text-muted" : ""}`}
                    type="time"
                    value={morningStart}
                    onChange={(event) => setMorningStart(event.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mt-3">
                  <Form.Label>{translate(language, "PAGES.COHORT.MEETINGS.MODAL.LABELS.MORNING_END")}</Form.Label>
                  <Form.Control
                    className={`border-secondary ${!morningEnd ? "text-muted" : ""}`}
                    type="time"
                    value={morningEnd}
                    onChange={(event) => setMorningEnd(event.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mt-3">
                  <Form.Label>{translate(language, "PAGES.COHORT.MEETINGS.MODAL.LABELS.AFTERNOON_START")}</Form.Label>
                  <Form.Control
                    className={`border-secondary ${!afternoonStart ? "text-muted" : ""}`}
                    type="time"
                    value={afternoonStart}
                    onChange={(event) => setAfternoonStart(event.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mt-3">
                  <Form.Label>{translate(language, "PAGES.COHORT.MEETINGS.MODAL.LABELS.AFTERNOON_END")}</Form.Label>
                  <Form.Control
                    className={`border-secondary ${!afternoonEnd ? "text-muted" : ""}`}
                    type="time"
                    value={afternoonEnd}
                    onChange={(event) => setAfternoonEnd(event.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mt-3">
                  <Form.Label>
                    <RequiredLabel label={translate(language, "PAGES.COHORT.MEETINGS.MODAL.LABELS.DAYS")} />
                  </Form.Label>
                  <MultiSelectDropdown
                    label={
                      selectedDays.length > 0
                        ? selectedDays.map((item) => item.day).join(", ")
                        : translate(language, "PAGES.COHORT.MEETINGS.MODAL.PLACEHOLDERS.SELECT_DAYS")
                    }
                    items={getDaysOptions(language)}
                    onConfirm={setSelectedDays}
                    badgeItemProperty="day"
                    displayItem={(item) => item.day}
                    language={language}
                    toggleClass="w-100 border-secondary"
                    btnWidth="100%"
                  />
                </Form.Group>
              </React.Fragment>
            ) : (
              <Form.Group className="mt-3">
                <Form.Label>
                  <RequiredLabel label={translate(language, "GLOBAL.DURATION")} />
                </Form.Label>
                <Form.Control
                  className={`border-secondary ${durationError ? "border-danger" : ""}`}
                  type="text"
                  value={duration}
                  onChange={handleDurationChange}
                />
                {durationError && (
                  <Form.Text className="text-danger">
                    {translate(language, "PAGES.COHORT.MEETINGS.ERRORS.UNFILLED_DURATION")}
                  </Form.Text>
                )}
              </Form.Group>
            )}

            <Form.Group className="mt-3">
              <Form.Label>
                <RequiredLabel label={translate(language, "PAGES.COHORT.MEETINGS.MODAL.LABELS.VISIBILITY")} />
              </Form.Label>
              <Dropdown>
                <Dropdown.Toggle
                  className="w-100 d-flex justify-content-between align-items-center border-secondary"
                  variant=""
                >
                  {visibilityOptions[selectedVisibility]}
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100">
                  <Dropdown.Item onClick={() => setSelectedVisibility("shared")}>
                    {translate(language, "PAGES.COHORT.MEETINGS.MODAL.OPTIONS.VISIBILITY.SHARED")}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setSelectedVisibility("private")}>
                    {translate(language, "PAGES.COHORT.MEETINGS.MODAL.OPTIONS.VISIBILITY.PRIVATE")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>{translate(language, "GLOBAL.ORGANIZER")}</Form.Label>
              <Form.Control className="border-secondary" type="text" readOnly value={email} disabled />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>{translate(language, "GLOBAL.ANIMATORS")}</Form.Label>
              <MultiSelectDropdown
                mock={animators.length === 0}
                btnWidth="100%"
                label={
                  selectedAnimators.length > 0
                    ? `${selectedAnimators.length} ${translate(
                        language,
                        `PAGES.COHORT.MEETINGS.MODAL.PLACEHOLDERS.${
                          selectedAnimators.length === 1 ? "ANIMATOR_SELECTED" : "ANIMATORS_SELECTED"
                        }`
                      )}`
                    : translate(language, "PAGES.COHORT.MEETINGS.MODAL.PLACEHOLDERS.ANIMATORS")
                }
                items={animators}
                onConfirm={setSelectedAnimators}
                badgeItemProperty="email"
                displayItem={(item) => item.email}
                language={language}
                showAlwaysConfirm
                toggleClass={`text-truncate w-100 border-secondary ${
                  selectedAnimators.length === 0 ? "text-muted" : ""
                }`}
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>
                {translate(language, "PAGES.COHORT.MEETINGS.MODAL.LABELS.SERVICE_LINK_GENERATION")}
              </Form.Label>
              <Dropdown>
                <Dropdown.Toggle
                  className="w-100 d-flex justify-content-between align-items-center border-secondary"
                  variant=""
                >
                  {meetingLinkService === "zoom" &&
                    translate(language, "PAGES.COHORT.MEETINGS.MODAL.OPTIONS.SERVICE_LINK_GENERATION.ZOOM")}
                  {meetingLinkService === "teams" &&
                    translate(language, "PAGES.COHORT.MEETINGS.MODAL.OPTIONS.SERVICE_LINK_GENERATION.TEAMS")}
                  {meetingLinkService === null &&
                    translate(language, "PAGES.COHORT.MEETINGS.MODAL.OPTIONS.SERVICE_LINK_GENERATION.NONE")}
                </Dropdown.Toggle>

                <Dropdown.Menu className="w-100">
                  <Dropdown.Item onClick={() => setMeetingLinkService("zoom")}>
                    {translate(language, "PAGES.COHORT.MEETINGS.MODAL.OPTIONS.SERVICE_LINK_GENERATION.ZOOM")}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setMeetingLinkService("teams")}>
                    {translate(language, "PAGES.COHORT.MEETINGS.MODAL.OPTIONS.SERVICE_LINK_GENERATION.TEAMS")}
                  </Dropdown.Item>

                  <Dropdown.Item onClick={() => setMeetingLinkService(null)}>
                    {translate(language, "PAGES.COHORT.MEETINGS.MODAL.OPTIONS.SERVICE_LINK_GENERATION.NONE")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>
            {meetingLinkService === null && (
              <Form.Group className="mt-3">
                <Form.Label>{translate(language, "PAGES.COHORT.MEETINGS.MODAL.LABELS.MEETING_LINK")}</Form.Label>
                <Form.Control
                  className={`border-secondary ${!url ? "text-muted" : ""}`}
                  type="text"
                  placeholder={translate(language, "PAGES.COHORT.MEETINGS.MODAL.PLACEHOLDERS.MEETING_LINK")}
                  value={url}
                  onChange={(event) => setUrl(event.target.value)}
                />
              </Form.Group>
            )}

            <Form.Group className="mt-3">
              <Form.Label>{translate(language, "PAGES.COHORT.MEETINGS.MODAL.LABELS.DESCRIPTION")}</Form.Label>
              <Form.Control
                className={`border-secondary ${!description ? "text-muted" : ""}`}
                as="textarea"
                rows={3}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </Form.Group>
          </Form>
          <Modal.Footer className="pt-5 border-top-0">
            <DstButton
              variant="secondary"
              value={translate(language, "GLOBAL.CANCEL")}
              clickFunction={() => {
                setShowModal(false);
                resetSelection();
              }}
            />
            <DstButton
              value={translate(language, "PAGES.COHORT.MEETINGS.MODAL.BUTTONS.CONFIRM")}
              clickFunction={handleConfirm}
              disabled={!isFormValid || isMeetingCreating}
              loading={isMeetingCreating}
            />
          </Modal.Footer>
        </Popup>
        <DstAlert show={showAlert} onHide={_handleAlertClose} {...alertProps} />
      </div>
    </React.Fragment>
  );
};

export default CohortMeetings;
